// ✅ src/pages/VehicleV2/PeriodicEntryReport_PrintRow.jsx
//
// ✅ What this page does
// - Uses the SAME HEADER style you attached (NISSI INFOTECH block)
// - Reads selected row id from URL and calls backend to fetch details
// - Shows the print layout like your screenshot (top details + table)
//
// ✅ URL formats supported:
// 1) /vehicle/PeriodicEntryReport_PrintRow?vehicleid=79&maintenanceid=12
//    -> calls GET  /api/periodicEntryReport/print/row/:vehicleid/:maintenanceid
//
// 2) /vehicle/PeriodicEntryReport_PrintRow?id=1016
//    -> calls GET  /api/periodicEntryReport/print/1016   (ONLY if you create this endpoint)
//    (I kept this optional for you)
//
// ✅ Backend (recommended):
// app.use("/api/periodicEntryReport", periodicEntryReport);
// periodicEntryReport should have:
//   GET /print/row/:vehicleid/:maintenanceid   => { success:true, row:{...} }
//
// ------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/periodicEntryReport";

const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};
const fmt2 = (v) =>
  toNum(v, 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PeriodicEntryReport_PrintRow() {
  const [sp] = useSearchParams();

  const id = toStr(sp.get("id")); // optional (if you implement /print/:id)
  const vehicleid = toStr(sp.get("vehicleid"));
  const maintenanceid = toStr(sp.get("maintenanceid"));

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");

    try {
      let url = "";

      if (id) {
        // ✅ Optional endpoint (only if you add it in backend):
        // GET /api/periodicEntryReport/print/:id
        url = `${API_BASE}/print/${encodeURIComponent(id)}`;
      } else {
        if (!vehicleid || !maintenanceid) {
          throw new Error("Missing vehicleid / maintenanceid in URL");
        }
        url = `${API_BASE}/print/row/${encodeURIComponent(vehicleid)}/${encodeURIComponent(
          maintenanceid
        )}`;
      }

      const { data } = await axios.get(url);
      if (!data?.success) throw new Error(data?.message || "Failed to fetch print details");

      setRow(data.row || data.data || data.record || null);

      // auto print (like ASP.NET window.open)
      setTimeout(() => window.print(), 400);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ map fields from backend row (safe fallbacks)
  const header = useMemo(() => {
    const r = row || {};
    return {
      reportDate: toStr(r.fld_curdate || r.fld_progressentrydate || r.fld_entrydate || r.Date || ""),
      branch: toStr(r.fld_branchname || r.branch || ""),
      equipmentCode: toStr(r.fld_vehicle_code || r.code || ""),
      regNo: toStr(r.fld_vehicle_regno || r.regNo || ""),
      vehicleName: toStr(r.fld_vehicle_name || r.name || ""),
      vehicleType: toStr(r.VehicleType || r.fld_vehicletypename || r.type || ""),
      vehicleSubType: toStr(r.SubType || r.fld_vehicle_subtypename || r.subType || ""),
    };
  }, [row]);

  // In most SP outputs, details may already be a single row.
  // If your backend returns {row, details:[...]} you can replace `detailsRows` with data.details.
  const detailsRows = useMemo(() => {
    if (!row) return [];
    // If backend returns array inside row.details, use it; else show single as one row.
    const arr = Array.isArray(row.details) ? row.details : [row];

    return arr.map((r, idx) => {
      const lastChange = r.fld_hrmtrreading ?? r.lastChanged ?? "";
      const usageLimit = r.fld_usagelimit_calc ?? r.fld_usagelimit ?? r.usageLimit ?? "";
      const nextCum = r.fld_usagelimit_calc ?? r.nextService ?? ""; // your screenshot shows "Next cumulative"
      const currentCum = r.fld_hrskm ?? r.currentCumulative ?? "";
      const remaining = r.fld_Remaininghr ?? r.remaining ?? "";
      const alertText = r.alertText || r.alert || "";

      return {
        sno: idx + 1,
        subAssembly: toStr(r.fld_vehicle_subassemblyname || r.subAssembly || ""),
        description: toStr(r.fld_Description || r.description || ""),
        oilGradePartNo: toStr(r.fld_partnumbergrade || r.oilGradePartNo || ""),
        lastChange,
        usageLimit,
        nextCum,
        currentCum,
        lastUpdateDate: toStr(r.fld_progressentrydate || r.fld_curdate || r.updateDate || ""),
        remaining,
        alert: toStr(alertText),
      };
    });
  }, [row]);

  if (loading) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        Loading print...
      </div>
    );
  }

  if (err || !row) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Print failed</div>
        <div style={{ color: "crimson" }}>{err || "No data"}</div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", fontFamily: "Arial, sans-serif" }}>
      {/* Print CSS */}
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display:none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 6px; font-size: 12px; }
        th { font-weight: 700; text-align: center; }
      `}</style>

      {/* Outer Border (like screenshot) */}
      <div style={{ border: "2px solid #000", padding: 10 }}>
        {/* HEADER (same idea as your attached code) */}
        <div style={{ border: "1px solid #000", padding: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Logo placeholder (replace with your logo image if you have) */}
           <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Nissi Infotech</div>
              <div style={{ fontWeight: 700, fontSize: 12, marginTop: 2 }}>Nissi</div>
              <div style={{ fontWeight: 700, fontSize: 12 }}>CIVIL CONSTRUCTION</div>

              <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.3 }}>
                <div>3/42-12, Nagappa Colony, kavundampalayam - 641030 . Tamil Nadu . India .</div>
                <div>Phone No : 9003744644</div>
                <div>infracnsoft-implementation@nissiinfotech.in</div>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div style={{ borderLeft: "1px solid #000", borderRight: "1px solid #000", borderBottom: "1px solid #000" }}>
          <div style={{ textAlign: "center", fontWeight: 700, padding: "8px 0" }}>
            Periodic Entry Report
          </div>
        </div>

        {/* Top Info (Date/Branch/Equipment/Type/RegNo/SubType/Name) */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, fontSize: 12 }}>
            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>Date</b> : {header.reportDate || "-"}
              </div>
              <div>
                <b>Equipment Code</b> : {header.equipmentCode || "-"}
              </div>
              <div>
                <b>Reg No</b> : {header.regNo || "-"}
              </div>
              <div>
                <b>Name</b> : {header.vehicleName || "-"}
              </div>
            </div>

            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>Branch</b> : {header.branch || "-"}
              </div>
              <div>
                <b>Type</b> : {header.vehicleType || "-"}
              </div>
              <div>
                <b>Sub Type</b> : {header.vehicleSubType || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>S.No</th>
                <th>Sub Assembly</th>
                <th>Description</th>
                <th>Oil/Grade part no</th>
                <th style={{ width: 90 }}>Last change Hr / KM</th>
                <th style={{ width: 80 }}>Usage Limit</th>
                <th style={{ width: 95 }}>Next cummulative Hr / KM</th>
                <th style={{ width: 110 }}>Current cummulative Hr / KM</th>
                <th style={{ width: 90 }}>Last Update Date</th>
                <th style={{ width: 85 }}>Remaining Hrs / KM</th>
                <th style={{ width: 90 }}>Alert</th>
              </tr>
            </thead>
            <tbody>
              {detailsRows.map((d) => (
                <tr key={d.sno}>
                  <td style={{ textAlign: "center" }}>{d.sno}</td>
                  <td style={{ textAlign: "center" }}>{d.subAssembly}</td>
                  <td style={{ textAlign: "center" }}>{d.description}</td>
                  <td style={{ textAlign: "center" }}>{d.oilGradePartNo}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.lastChange)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.usageLimit)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.nextCum)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.currentCum)}</td>
                  <td style={{ textAlign: "center" }}>{d.lastUpdateDate}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.remaining)}</td>
                  <td style={{ textAlign: "center" }}>{d.alert}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Blank space area like your screenshot */}
          <div style={{ height: 220, borderTop: "1px solid #000" }} />
        </div>

        {/* Footer line */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 10, fontSize: 11 }}>
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>

        {/* Print button (hidden in print) */}
        <div className="no-print" style={{ marginTop: 12, textAlign: "center" }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid #333",
              cursor: "pointer",
              background: "#1d4ed8",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Print
          </button>
        </div>
      </div>
    </div>
  );
}
